import Breadcrumbs from '../../../components/common/breadcrumbs.tsx';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import {
  GET_TEST,
  TEST_FRAGMENT,
  UPDATE_TEST,
  TEST_SECTION_FRAGMENT,
} from '../../../apollo/tests.ts';
import { useFragment } from '../../../apollo/__generated__';
import { useNavigate, useParams } from 'react-router-dom';
import { useRemoveTests } from '../../../components/tests/tests-hooks.tsx';
import Select from '../../../components/common/select.tsx';
import {
  TestSectionFragment,
  TestStatus,
  UserActivityLogType,
} from '../../../apollo/__generated__/graphql.ts';
import { toast } from 'react-toastify';
import InfoTable, {
  IInfoTable,
} from '../../../components/common/info-table.tsx';
import TestSectionItem from '../../../components/tests/sections/test-section-item.tsx';
import EditIcon from '../../../assets/icons/edit.svg?react';
import { useCallback, useMemo, useState } from 'react';
import Modal from '../../../components/common/modal.tsx';
import CreateTestForm from '../../../components/tests/create-test-form.tsx';
import RemoveModal from '../../../components/common/remove-modal.tsx';
import RemoveButton from '../../../components/common/remove-button.tsx';
import {
  IQuestionsExportSettingsFormData,
  QuestionsExportSettingsForm,
} from '../../../components/questions/questions-export-settings-form.tsx';
import { Popover } from '../../../components/common/popover.tsx';
import { useGetMe } from '../../../utils/hooks.ts';
import ActionsDropdown from '../../../components/table/actions-dropdown.tsx';
import { createPdfExportUrl } from '../../../utils/common-utils.ts';
import EditMistakesAmount from '../../../components/tests/sections/edit-mistakes-amount.tsx';
import { capitalizeWords } from '../../../utils/capitalize-words.ts';
import { NavBarEnum } from '../../../utils/enums.ts';
import { useCreateExportLog } from '../../../hooks/use-create-export-log.ts';

const TestInformation = () => {
  const { isAdmin, isSales, isTA, isTeacher } = useGetMe();
  const canEdit = isAdmin || isTA || isTeacher;
  const canExport = !isSales;
  const navigate = useNavigate();
  const params = useParams();
  const testSlug = params?.testSlug;
  const redirect = () => navigate('/tests-browser', { replace: true });
  const remove = useRemoveTests(redirect);
  const { data } = useSuspenseQuery(GET_TEST, {
    variables: { input: { slug: testSlug } },
    errorPolicy: 'all',
  });

  const [editField, setEditField] = useState<
    'name' | 'description' | 'notes' | null
  >(null);
  const [editMistakesModal, setEditMistakesModal] = useState(false);
  const [selectedModules, setSelectedModules] = useState<number[]>([]);
  const [section, setSection] = useState<TestSectionFragment>();
  const createExportLog = useCreateExportLog();

  const test = useFragment(TEST_FRAGMENT, data?.test.getTest);
  const sections = useFragment(TEST_SECTION_FRAGMENT, test?.sections);
  const [update] = useMutation(UPDATE_TEST);

  const onChangeStatus = useCallback(
    (status: TestStatus) => {
      if (!test) return null;
      const updatePromise = update({
        variables: {
          input: {
            name: test.name,
            status: status,
            id: test.id,
          },
        },
      });

      toast.promise(updatePromise, {
        success: 'Test status changed',
        pending: 'Updating test status...',
      });
    },
    [test, update],
  );

  const onCloseFieldModal = () => setEditField(null);
  const onCloseMistakesModal = () => setEditMistakesModal(false);
  const onUpdateTestData = (slug: string) => {
    onCloseFieldModal();
    navigate(`/tests-browser/${slug}`, { replace: true });
  };
  const onEditMistakesAmount = (section: TestSectionFragment) => {
    setSection(section);
    setEditMistakesModal(true);
  };

  const fieldCardClass =
    'flex min-h-11 items-center rounded-md border border-table-border bg-[#FAFAFA] px-3 py-2';
  const editButtonClass =
    'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-[#D6D9E4] bg-white text-gray transition hover:border-[#9BA3B5] hover:text-black';
  const statusSelectClass =
    test?.status === TestStatus.Active
      ? 'min-w-28 rounded bg-green py-1 pl-3 pr-10 text-xs font-medium text-white'
      : 'min-w-28 rounded bg-[#D3DCEC] py-1 pl-3 pr-10 text-xs font-medium text-dark-blue';

  const table = useMemo(() => {
    const result: IInfoTable = [
      {
        head: 'Name',
        value: (
          <div className={`${fieldCardClass} justify-between gap-3`}>
            <span className={'font-medium text-black'}>{test?.name}</span>
            {canEdit && (
              <button
                className={editButtonClass}
                onClick={() => setEditField('name')}
              >
                <EditIcon />
              </button>
            )}
          </div>
        ),
      },
      {
        head: 'Description',
        value: (
          <div className={`${fieldCardClass} justify-between gap-3`}>
            <span className={'text-black'}>{test?.description || ''}</span>
            {canEdit && (
              <button
                className={editButtonClass}
                onClick={() => setEditField('description')}
              >
                <EditIcon />
              </button>
            )}
          </div>
        ),
      },
      {
        head: 'Notes',
        value: (
          <div className={`${fieldCardClass} justify-between gap-3`}>
            <span className={'text-black'}>{test?.notes || ''}</span>
            {canEdit && (
              <button
                className={editButtonClass}
                onClick={() => setEditField('notes')}
              >
                <EditIcon />
              </button>
            )}
          </div>
        ),
      },
      {
        head: 'Total sections',
        value: (
          <div className={`${fieldCardClass} w-fit min-w-24 justify-center`}>
            <span className={'text-lg font-semibold text-black'}>
              {test?.sectionCount}
            </span>
          </div>
        ),
      },
    ];
    const rwSection = sections?.find(
      (item) => item.section.name === 'reading and writing',
    );
    const mathSection = sections?.find((item) => item.section.name === 'math');
    if (rwSection || mathSection) {
      result.push({
        head: 'Minimum Mistakes to Show Easy Module 2',
        value: (
          <div className={'flex items-center gap-4'}>
            {rwSection && (
              <div
                className={
                  `${fieldCardClass} gap-3`
                }
              >
                <span className={'text-sm font-medium text-gray'}>
                  Reading and Writing
                </span>
                <span className={'text-lg font-semibold text-black'}>
                  {rwSection.minWrongAnswersForEasyModule ?? 0}
                </span>
                {canEdit && (
                  <button
                    className={editButtonClass}
                    onClick={() => onEditMistakesAmount(rwSection)}
                  >
                    <EditIcon />
                  </button>
                )}
              </div>
            )}
            {mathSection && (
              <div
                className={
                  `${fieldCardClass} gap-3`
                }
              >
                <span className={'text-sm font-medium text-gray'}>Math</span>
                <span className={'text-lg font-semibold text-black'}>
                  {mathSection.minWrongAnswersForEasyModule ?? 0}
                </span>
                {canEdit && (
                  <button
                    className={editButtonClass}
                    onClick={() => onEditMistakesAmount(mathSection)}
                  >
                    <EditIcon />
                  </button>
                )}
              </div>
            )}
          </div>
        ),
      });
    }
    result.push(
      {
        head: 'Status',
        value: (
          <div className={`${fieldCardClass} w-fit min-w-48`}>
            <Select
              value={test?.status || TestStatus.Draft}
              options={[TestStatus.Active, TestStatus.Draft].map((status) =>
                capitalizeWords(status),
              )}
              onChange={(value) => onChangeStatus(value as TestStatus)}
              disabled={!canEdit}
              className={statusSelectClass}
            />
          </div>
        ),
      },
      {
        head: 'Create by',
        value: (
          <div className={`${fieldCardClass} w-fit min-w-48`}>
            <span className={'text-black'}>
              {test?.createdBy?.name || '(Deleted user)'}
            </span>
          </div>
        ),
      },
    );

    return result;
  }, [
    canEdit,
    editButtonClass,
    fieldCardClass,
    onChangeStatus,
    sections,
    statusSelectClass,
    test,
  ]);

  const onExport = useCallback(
    (params: IQuestionsExportSettingsFormData) => {
      if (!test?.id) return null;
      void createExportLog({
        testId: test.id,
        type: UserActivityLogType.ExportPdf,
      });
      const url = createPdfExportUrl('test', {
        id: test.id.toString(),
        moduleIds: selectedModules.join(','),
        ...params,
      });
      window.open(url);
    },
    [createExportLog, selectedModules, test?.id],
  );

  return (
    <div>
      <Breadcrumbs
        elements={[
          { title: 'Tests Browser', href: '/tests-browser' },
          { title: test?.name || '' },
        ]}
      />

      <section className={'mt-4 flex items-center justify-between'}>
        <div>
          <h1>Test Information</h1>
        </div>
        <div className={'flex gap-2'}>
          <ActionsDropdown
            selected={test ? [test?.id] : []}
            browser={NavBarEnum.TESTS_BROWSER}
          />
          {canExport && (
            <Popover
              triggerComponent={
                <button
                  className={
                    'box-border flex h-fit items-center justify-center rounded bg-light-blue p-2 text-sm font-medium text-black'
                  }
                >
                  Export
                </button>
              }
              contentProps={{ className: 'w-80' }}
            >
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Export</h4>
                  <p className={'description'}>Set the test export settings</p>
                </div>
                <QuestionsExportSettingsForm onSubmit={onExport} />
              </div>
            </Popover>
          )}
          {canEdit && !isTA && <RemoveButton onClick={remove.onOpenModal} />}
        </div>
      </section>

      <InfoTable table={table} className={'mt-2'} />

      <section className={'mt-6'}>
        <h1>Sections</h1>

        <div className={'mt-2 flex flex-col gap-2'}>
          {sections?.map((section) => {
            return (
              <TestSectionItem
                key={section.id}
                section={section}
                testSlug={test?.slug}
                testId={test?.id}
                selectedModules={selectedModules}
                setSelectedModules={setSelectedModules}
                defaultOpen
              />
            );
          })}
        </div>
      </section>

      <Modal
        title={
          editField === 'name'
            ? 'Edit Name'
            : editField === 'description'
              ? 'Edit Description'
              : 'Edit Notes'
        }
        onClose={onCloseFieldModal}
        isOpen={!!editField}
      >
        <CreateTestForm
          test={test}
          onCloseModal={onUpdateTestData}
          editField={editField || undefined}
        />
      </Modal>

      <Modal
        title={`Edit ${capitalizeWords(section?.section.name)} Minimum Mistakes`}
        onClose={onCloseMistakesModal}
        isOpen={editMistakesModal}
      >
        {section && (
          <EditMistakesAmount
            id={section.id}
            onCloseModal={onCloseMistakesModal}
            amount={section?.minWrongAnswersForEasyModule ?? 0}
          />
        )}
      </Modal>
      <RemoveModal
        onRemove={() => remove.onRemoveTests(test ? [test.id] : [])}
        onClose={remove.onCloseModal}
        open={remove.isModalOpen}
      />
    </div>
  );
};

export default TestInformation;
